import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { describeLoadFailure, jsonOrThrow } from '../apiJson';
import { trackGenerateLead } from '../ga';
import {
  clearProductWatching,
  getSavedAlertEmail,
  isProductWatching,
  markProductWatching,
  setSavedAlertEmail,
} from '../productWatchIntent';
import './ProductCardReturnCta.css';

const eurPriceFormatter = new Intl.NumberFormat('mt-MT', {
  style: 'currency',
  currency: 'EUR',
});

/**
 * @param {{ items: string[] }} props
 */
function ReturnCtaBenefits({ items }) {
  return (
    <ul className="product-card__return-cta-benefits">
      {items.map((text) => (
        <li key={text} className="product-card__return-cta-benefit">
          {text}
        </li>
      ))}
    </ul>
  );
}

/**
 * @param {{ items: string[] }} props
 */
function ReturnCtaSummary({ items }) {
  return (
    <p className="product-card__return-cta-summary">
      {items.join(' \u00b7 ')}
    </p>
  );
}

/**
 * @param {{
 *   alertKind: object;
 *   emailCopy: object;
 *   step: 'email' | 'done';
 *   titleId: string;
 *   emailId: string;
 *   emailRef: import('react').RefObject<HTMLInputElement|null>;
 *   email: string;
 *   setEmail: (value: string) => void;
 *   submitting: boolean;
 *   error: string|null;
 *   primaryLabel: string;
 *   heading: string|null;
 *   subline: string|null;
 *   benefits: string[]|null;
 *   onClose: () => void;
 *   onEmailSubmit: (e: import('react').FormEvent) => void;
 *   onDoneDismiss: () => void;
 * }} props
 */
function ReturnCtaModal({
  alertKind,
  emailCopy,
  step,
  titleId,
  emailId,
  emailRef,
  email,
  setEmail,
  submitting,
  error,
  primaryLabel,
  heading,
  subline,
  benefits,
  onClose,
  onEmailSubmit,
  onDoneDismiss,
}) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (step !== 'email') {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      emailRef.current?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [step, emailRef]);

  return createPortal(
    <div
      className="return-cta-modal__backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="return-cta-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="return-cta-modal__header">
          <p className="return-cta-modal__kicker">{alertKind.shortLabel}</p>
          {heading != null && (
            <h2 id={titleId} className="return-cta-modal__heading">
              {heading}
            </h2>
          )}
          <button
            type="button"
            className="return-cta-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {subline != null && (
          <p className="return-cta-modal__subline">{subline}</p>
        )}

        {step === 'email' && benefits != null && benefits.length > 0 && (
          <ReturnCtaSummary items={benefits} />
        )}

        {step === 'done' && benefits != null && benefits.length > 0 && (
          <ReturnCtaBenefits items={benefits} />
        )}

        <div className="return-cta-modal__actions">
          {step === 'email' && (
            <>
              {error !== null && (
                <p className="return-cta-modal__error" role="alert">
                  {error}
                </p>
              )}
              <form
                className="return-cta-modal__form"
                onSubmit={onEmailSubmit}
                noValidate
              >
                <label className="sr-only" htmlFor={emailId}>
                  Email for product alerts
                </label>
                <input
                  ref={emailRef}
                  id={emailId}
                  name="email"
                  className="return-cta-modal__input"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  required
                  maxLength={255}
                  disabled={submitting}
                />
                <button
                  type="submit"
                  className="return-cta-modal__submit"
                  disabled={submitting}
                >
                  {submitting ? emailCopy.saving : primaryLabel}
                </button>
              </form>
            </>
          )}

          {step === 'done' && (
            <button
              type="button"
              className="return-cta-modal__submit"
              onClick={onDoneDismiss}
            >
              {primaryLabel}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * @param {{
 *   productUrl: string;
 *   inStock?: boolean|null;
 *   priceUnknown?: boolean;
 *   displayNow?: number|null;
 *   onDismiss: () => void;
 * }} props
 */
export function ProductCardReturnCta({
  productUrl,
  inStock,
  priceUnknown = false,
  displayNow = null,
  onDismiss,
}) {
  const titleId = useId();
  const emailId = useId();
  const emailRef = useRef(null);
  const isStockAlert = inStock === false;

  const [step, setStep] = useState(() =>
    isProductWatching(productUrl) ? 'email' : 'invite',
  );
  const [email, setEmail] = useState(() => getSavedAlertEmail());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const priceLabel =
    !priceUnknown
    && displayNow != null
    && !Number.isNaN(Number(displayNow))
      ? eurPriceFormatter.format(Number(displayNow))
      : null;

  const alertKind = isStockAlert
    ? {
        shortLabel: 'Back-in-stock alert',
        inviteAction: 'Notify when back in stock',
        emailHeading: 'Where should we send it?',
        emailBenefits: [
          'We send an email when this product is back in stock. Unsubscribe anytime.',
        ],
        doneTrigger: 'when this is back in stock',
      }
    : priceUnknown
      ? {
          shortLabel: 'Price alert',
          inviteAction: 'Notify when there\u2019s a price',
          emailHeading: 'Where should we send it?',
          emailBenefits: [
            'We send an email when a price appears for this product. Unsubscribe anytime.',
          ],
          doneTrigger: 'when a price appears',
        }
      : {
          shortLabel: 'Price drop alert',
          inviteAction: priceLabel != null
            ? `Notify if price drops below ${priceLabel}`
            : 'Notify if price drops',
          emailHeading: 'Where should we send it?',
          emailBenefits: [
            'We send an email if the price of this product drops. Unsubscribe anytime.',
          ],
          doneTrigger: priceLabel != null
            ? `if ${priceLabel} drops`
            : 'if the price drops',
        };

  const inviteCopy = {
    action: alertKind.inviteAction,
  };

  const emailCopy = {
    heading: alertKind.emailHeading,
    benefits: alertKind.emailBenefits,
    action: 'Notify me',
    saving: 'Saving\u2026',
  };

  function buildDoneCopy(email) {
    const emailBit = email !== '' ? email : 'your email';

    return {
      heading: 'Thanks for signing up',
      subline: `Sorry \u2014 alerts aren\u2019t live yet. We\u2019ve saved ${emailBit} and will email you ${alertKind.doneTrigger} once they\u2019re ready.`,
      benefits: null,
    };
  }

  function handleInviteDismiss(e) {
    e.preventDefault();
    e.stopPropagation();
    onDismiss();
  }

  function handleWatch(e) {
    e.preventDefault();
    e.stopPropagation();
    markProductWatching(productUrl);
    setStep('email');
    setError(null);
  }

  function handleModalClose() {
    if (step === 'done') {
      onDismiss();
      return;
    }

    clearProductWatching(productUrl);
    setError(null);
    setStep('invite');
  }

  function handleDoneDismiss() {
    onDismiss();
  }

  async function handleEmailSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    setError(null);
    setSubmitting(true);

    const trimmedEmail = email.trim();

    try {
      const res = await fetch('/api/product-favorite-signup.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          productUrl,
        }),
      });
      await jsonOrThrow(res);
      setSavedAlertEmail(trimmedEmail);
      clearProductWatching(productUrl);
      setSubmittedEmail(trimmedEmail);
      setStep('done');
      trackGenerateLead('product_price_alert');
    } catch (err) {
      setError(describeLoadFailure(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (step === 'email' || step === 'done') {
    let heading = null;
    let subline = null;
    let benefits = null;
    let primaryLabel = emailCopy.action;

    if (step === 'email') {
      heading = emailCopy.heading;
      benefits = emailCopy.benefits;
      primaryLabel = emailCopy.action;
    } else {
      const doneCopy = buildDoneCopy(submittedEmail);
      heading = doneCopy.heading;
      subline = doneCopy.subline;
      benefits = doneCopy.benefits;
      primaryLabel = 'Got it';
    }

    return (
      <ReturnCtaModal
        alertKind={alertKind}
        emailCopy={emailCopy}
        step={step}
        titleId={titleId}
        emailId={emailId}
        emailRef={emailRef}
        email={email}
        setEmail={setEmail}
        submitting={submitting}
        error={error}
        primaryLabel={primaryLabel}
        heading={heading}
        subline={subline}
        benefits={benefits}
        onClose={handleModalClose}
        onEmailSubmit={handleEmailSubmit}
        onDoneDismiss={handleDoneDismiss}
      />
    );
  }

  return (
    <div
      className="product-card__return-cta product-card__return-cta--invite"
      role="dialog"
      aria-label={alertKind.shortLabel}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="product-card__return-cta-close"
        onClick={handleInviteDismiss}
        aria-label="Close"
      >
        ×
      </button>

      <div className="product-card__return-cta-copy">
        <p className="product-card__return-cta-kicker">{alertKind.shortLabel}</p>
      </div>

      <div className="product-card__return-cta-actions">
        <button
          type="button"
          className="product-card__return-cta-notify"
          onClick={handleWatch}
        >
          {inviteCopy.action}
        </button>
      </div>
    </div>
  );
}
